<template>
    <widget-card
        colClasses="xs12"
        :heading="widget.text.caption[$i18n.locale]" 
        custom-card-classes="chart-wrapper">
        <bar-chart-agency 
            class="chart-wrapper" 
            :sets="series" 
            :is-money-value="isMoneyValue" 
            :styles="myStyles">
        </bar-chart-agency>
    </widget-card>
</template>

<script>
import Enumerable from 'linq'
import BarChartAgency from './BarChartAgency.js'
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'

export default {
    props: {
        widget: {
            type: Object,
            required: true
        }
    },
    components: {
        BarChartAgency
    },
    computed: {
        ...mapGetters([
            'season',
            'reportingData',
            'lastSeasonReportingData',
            'selectedF1Event',
            'contractualContingent',
            'contractualContingentByEvent'
        ]),
        myStyles () {
            return {
                height: `95%`,
                position: 'relative'
            }
        },
        series() {
            // Filtere die relevanten Daten basierend auf dem ausgewählten Event und AgencyName
            const filteredData = this.reportingData.filter(item => {
                // Prüfe, ob im ShopName "Agency" vorkommt
                if (!item.ShopName || !item.ShopName.includes('Agency')) return false;
                
                // Dann prüfe das ausgewählte Event
                if (this.selectedF1Event === 'all') return true;
                return item.event_group_id === parseInt(this.selectedF1Event);
            }).filter(item => 
                // Nur Multi-Day Tickets berücksichtigen
                item.FK_ArticleType === 1 || item.FK_ArticleType === 2
            );

            // Gruppiere nach Agencies statt Partners
            let enumerable = Enumerable.from(filteredData);
            
            // Berechne verkaufte Tickets pro Agency
            let usedData = enumerable
                .groupBy(
                    item => item.wo_partner_name,
                    null,
                    (key, g) => ({
                        label: key,
                        fromDate: g.first().fromDate,
                        value: g.sum(x => parseInt(x.Menge) || 0),
                        color: getPartnerColor(key, 'used')
                    })
                )
                .orderBy(x => x.label)
                .toArray();

            // Ziel-Kontingente pro Agency basierend auf contractualContingent und contractualContingentByEvent
            let totalData = usedData.map(agency => {
                let contingent;
                if (this.selectedF1Event === 'all') {
                    contingent = this.contractualContingent[agency.label];
                } else {
                    // Find contingent for specific agency and event
                    contingent = Object.values(this.contractualContingentByEvent).find(c => 
                        c.partner_name === agency.label && 
                        String(c.FK_EventGroup) === this.selectedF1Event
                    );
                }
                return {
                    label: agency.label,
                    fromDate: agency.fromDate,
                    value: contingent ? (this.selectedF1Event === 'all' ? contingent.contractualTickets : contingent.ContingentContractual) : 0,
                    color: getPartnerColor(agency.label, 'total')
                };
            });

            return [
                {
                name: 'Target Sales',
                data: totalData,
                },
                {
                name: 'Sold Tickets',
                data: usedData,
                }
            ];
        },
        isMoneyValue: function () {
            return this.widget.data.sumField ? this.widget.data.sumField.isMoneyValue : false
        }
    },
    created() {
        this.loadContractualContingent();
        this.loadContractualContingentByEvent();
    },
    methods: {
        ...mapActions([
            'loadContractualContingent',
            'loadContractualContingentByEvent'
        ])
    }
}

// Hilfsfunktion für Partner-spezifische Farben
function getPartnerColor(partnerName, type) {
    const colorMap = {
        'F1 Experiences': { used: '#4CAF50', total: '#A5D6A7' },
        'Canada Sports Marketing': { used: '#2196F3', total: '#90CAF9' },
        'Edge Global': { used: '#9C27B0', total: '#E1BEE7' },
        'Hidden Meadow': { used: '#FF9800', total: '#FFE0B2' },
        'Sportvac': { used: '#F44336', total: '#FFCDD2' },
        'CSM': { used: '#009688', total: '#B2DFDB' },
        'ZK': { used: '#795548', total: '#D7CCC8' },
        'BAM': { used: '#607D8B', total: '#CFD8DC' },
        'Grand Prix Tickets': { used: '#E91E63', total: '#F8BBD0' },
        'P1': { used: '#673AB7', total: '#D1C4E9' },
        'Unknown': { used: '#9E9E9E', total: '#EEEEEE' }
    };

    return (colorMap[partnerName] && colorMap[partnerName][type]) || colorMap['Unknown'][type];
}
</script>

<style>
.chart-wrapper {
    position: relative;
    height: 95%;
    width: 100%;
}
</style>